import { DocumentData, DocumentSnapshot, Unsubscribe } from 'firebase/firestore';
import { LiveDataState } from 'hooks/useLiveData';
import { useEffect, useRef, useState } from 'react';
import { getDocument, subscribeToDocument } from 'services';

type State = LiveDataState;

/**
 * Convenience hook for fetching/subscribing to firestore document data.
 *
 * @example
 * // selectedArtist comes from artist dropdown menu.
 * const {
 *   state: artistDocumentState,
 *   data: artistDocumentData,
 *   error: artistDocumentError,
 * } = useDocument(`usersWeb/${selectedArtist?.value}`, true);
 *
 * @param path POSIX style document path.
 * @param subscribe Whether or not to watch for document changes.
 */
export const useDocument = <Model = DocumentData>(path: string | null, subscribe: boolean) => {
  const [state, setState] = useState<State>('pending');
  const [snapshot, setSnapshot] = useState<DocumentSnapshot<Model> | null>(null);
  const [data, setData] = useState<DocumentData | null>(null);
  const [error, setError] = useState<any>(null);

  const unsubscribe = useRef<Unsubscribe | null>(null);

  useEffect(() => {
    if (unsubscribe.current) {
      unsubscribe.current();
    }

    if (!path) {
      setState('pending');
      setSnapshot(null);
      setData(null);
      setError(null);
      return;
    }

    setState('loading');

    if (subscribe) {
      unsubscribe.current = subscribeToDocument<Model>(
        path,
        (snapshot) => {
          setSnapshot(snapshot);
          setData((snapshot.data() as Model) || null);
          setState('success');
        },
        (error) => {
          setError(error);
          setState('error');
        },
      );
    } else {
      getDocument<Model>(path)
        .then((snapshot) => {
          setSnapshot(snapshot);
          setData((snapshot.data() as Model) || null);
          setState('success');
        })
        .catch((error) => {
          setError(error);
          setState('error');
        });
    }

    return () => {
      unsubscribe.current && unsubscribe.current();
    };
  }, [path, subscribe]);

  return {
    state,
    data,
    snapshot,
    unsubscribe,
    error,
  };
};
